import React, { useCallback, useMemo } from "react";
import { useRemoteSensors } from "@/hooks/useRemoteSensors";
import { t, Trans } from "@lingui/macro";
import { MenuList, MenuListItemVariant, BlueSwitch, MenuListItem } from "@WahooFitness/redesignr";
import {
  Box,
  FormControl,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Typography,
  useTheme,
} from "@mui/material";
import { crux_sensor_product_type_e } from "@WahooFitness/crux-js-display-cfg";
import WSMSensorProductType from "@WahooFitness/wsm-native/dist/esm/types/product_type";

interface PowerMeterConfigProps {
  appToken: string;
  remoteSensorId: number;
}

const PowerMeterConfig: React.FC<PowerMeterConfigProps> = ({
  appToken,
  remoteSensorId,
}: PowerMeterConfigProps) => {
  const { remoteSensorStatus, saveRemoteSensorConfig } = useRemoteSensors(appToken);
  const { palette } = useTheme();

  const remoteSensor = remoteSensorStatus?.sensors?.find(
    (sensor) => sensor?.cfg?.sensor_id === remoteSensorId
  );

  const supportsCrankLength = useMemo(
    () => remoteSensor?.cfg?.crank_length_m !== undefined,
    [remoteSensor?.cfg?.crank_length_m]
  );

  const supportsPowerMeterCalibration = useMemo(
    () => remoteSensor?.cfg?.pm_auto_calib !== undefined,
    [remoteSensor?.cfg?.pm_auto_calib]
  );

  const crankLengthMM = useMemo(
    () => (remoteSensor?.cfg?.crank_length_m ? remoteSensor?.cfg?.crank_length_m * 1000.0 : 0),
    [remoteSensor?.cfg?.crank_length_m]
  );

  const shouldHideCrankLengthForSensor = useMemo(
    () =>
      [crux_sensor_product_type_e.WAHOO_KICKR_SNAP].includes(
        remoteSensor?.status.sensor_product_type as WSMSensorProductType
      ),
    [remoteSensor?.status.sensor_product_type]
  );

  const crankLengthSelectionOptions = [
    [140, "140mm"],
    [142.5, "142.5mm"],
    [145, "145mm"],
    [147.5, "147.5mm"],
    [150, "150mm"],
    [152.5, "152.5mm"],
    [155, "155mm"],
    [157.5, "157.5mm"],
    [160, "160mm"],
    [162.5, "162.5mm"],
    [165, "165mm"],
    [167.5, "167.5mm"],
    [170, "170mm"],
    [172.5, "172.5mm"],
    [175, "175mm"],
    [177.5, "177.5mm"],
    [180, "180mm"],
    [182.5, "182.5mm"],
    [185, "185mm"],
    [187.5, "187.5mm"],
    [190, "190mm"],
    [192.5, "192.5mm"],
    [195, "195mm"],
    [197.5, "197.5mm"],
    [200, "200mm"],
    [202.5, "202.5mm"],
    [205, "205mm"],
    [207.5, "207.5mm"],
    [210, "210mm"],
  ];

  const handleCrankLengthChange = useCallback(
    (event: SelectChangeEvent<number>) => {
      const lengthInMM = +event.target.value;
      const lengthInM = lengthInMM / 1000.0;
      saveRemoteSensorConfig(remoteSensorId, {
        crank_length_m: lengthInM,
      });
    },
    [remoteSensorId, saveRemoteSensorConfig]
  );

  return (
    <Box width="100%" display="flex" flexDirection="column" mt={-2}>
      <Box
        mb={
          supportsPowerMeterCalibration && supportsCrankLength && !shouldHideCrankLengthForSensor
            ? 4
            : 0
        }
      >
        {supportsCrankLength && !shouldHideCrankLengthForSensor && (
          <Box>
            <MenuListItem
              id="crank_length"
              content={
                <Typography variant="ui-base-medium">
                  <Trans>Crank length</Trans>
                </Typography>
              }
              variant={MenuListItemVariant.NoAction}
              hidden={!supportsCrankLength}
              hasDivider={false}
            />
            <FormControl
              component={Paper}
              elevation={4}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "& .MuiOutlinedInput-root": {
                  boxShadow: "none",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                },
              }}
            >
              <Select
                labelId="crank-length-select-label"
                id="crank-length-select"
                value={crankLengthMM}
                label={t`Crank length`}
                renderValue={(value) => {
                  const option = crankLengthSelectionOptions.find(([length]) => length === value);
                  return option ? (
                    <Typography variant="prose-base-medium">{option[1]}</Typography>
                  ) : (
                    value
                  );
                }}
                onChange={handleCrankLengthChange}
                disabled={remoteSensor?.cfg?.crank_length_m === undefined}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300, // Set the maximum height in pixels
                      marginTop: 10,
                      overflowY: "auto", // Enable vertical scrolling
                    },
                  },
                }}
              >
                {crankLengthSelectionOptions.map(([length, displayLength], index, array) => (
                  <MenuItem
                    key={length}
                    value={length}
                    sx={{
                      borderBottom: index === array.length - 1 ? "none" : "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    {displayLength}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </Box>

      <MenuList
        listItems={[
          {
            id: "power_meter_calibration",
            content: t`Automatic calibration`,
            disabled: remoteSensor?.cfg?.pm_auto_calib === undefined,
            variant: MenuListItemVariant.Action,
            actionComponent: (
              <Box display="flex" flexDirection="row" gap={1}>
                <BlueSwitch checked={remoteSensor?.cfg?.pm_auto_calib} />
              </Box>
            ),
            action: () => {
              saveRemoteSensorConfig(remoteSensorId, {
                pm_auto_calib: !remoteSensor?.cfg?.pm_auto_calib,
              });
            },
            hidden: !supportsPowerMeterCalibration,
          },
        ]}
        disableGutters
      />
      {supportsPowerMeterCalibration && (
        <Box mx={2}>
          <Typography variant="ui-base-medium" color={palette.text.secondary}>
            <Trans>
              When enabled, SPEEDPLAY POWR will perform a self-calibration process. This replaces
              the manual calibration, which should be performed every few rides.
            </Trans>
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export { PowerMeterConfig };
